<template>
  <sui-form-field v-if="!field.hidden">
    <sui-table compact celled size="small" :color="color">
      <sui-table-header>
        <sui-table-row>
          <sui-table-header-cell>
            From (host path)
            <span class="tooltip" data-inverted data-position="top center" :data-tooltip="'Be careful with manual changes!\nThey may break the whole host!'" style="float: right"><i class="warning sign yellow fitted icon" /></span>
          </sui-table-header-cell>
          <sui-table-header-cell>
            To (container path)
          </sui-table-header-cell>
          <sui-table-header-cell :width="2" v-if="field.uid">
            UID
            <span class="tooltip" data-inverted data-position="top right" :data-tooltip="'Owner\'s UID\nroot is 0'" style="float: right"><i class="help circle blue fitted icon" /></span>
          </sui-table-header-cell>
          <sui-table-header-cell collapsing>
            Sync
            <a class="tooltip" href="https://docs.d2c.io/getting-started/containers/#synchronizing" target="_blank" data-inverted data-position="top right" :data-tooltip="'Sync between containers\n(click to get more info)'" style="float: right"><i class="help circle blue fitted icon" /></a>
          </sui-table-header-cell>
          <sui-table-header-cell collapsing />
        </sui-table-row>
      </sui-table-header>
      <sui-table-body v-if="demoVolumes.length">
        <sui-table-row v-for="(volume, index) in demoVolumes" :key="index">
          <sui-table-cell>
            <input readonly :value="volume.src" tabindex="-1" />
          </sui-table-cell>
          <sui-table-cell>
            <input readonly :value="volume.dst" tabindex="-1" />
          </sui-table-cell>
          <sui-table-cell v-if="field.uid">
            <input readonly :value="volume.uid" tabindex="-1" />
          </sui-table-cell>
          <sui-table-cell collapsing>
            <sui-checkbox toggle disabled tabindex="-1" />
          </sui-table-cell>
          <sui-table-cell collapsing>
            <i class="trash fitted disabled icon" />
          </sui-table-cell>
        </sui-table-row>
      </sui-table-body>
      <sui-table-body v-if="volumes.length">
        <sui-table-row :warning="volume.hasWarning" :error="volume.hasError" v-for="(volume, index) in volumes" :key="index">
          <sui-table-cell>
            <div class="ui icon input" data-inverted :data-tooltip="volume.errors.src">
              <input ref="src" :readonly="volume.readonly" v-model="$attrs.value[index].src" @input="onInput()" :tabindex="volume.readonly ? -1 : 0" />
              <i class="warning link icon" title="Click to generate automatically" v-if="!volume.readonly" @click="lockSrc(index)" />
              <i class="lock link icon" title="Click to enter manually" v-if="volume.readonly" @click="unlockSrc(index)" />
            </div>
          </sui-table-cell>
          <sui-table-cell>
            <div class="ui input" data-inverted :data-tooltip="volume.errors.dst">
              <input ref="dst" v-model="$attrs.value[index].dst" @blur="onBlurDst(index)" @input="onInputDst(index)" />
            </div>
          </sui-table-cell>
          <sui-table-cell v-if="field.uid">
            <div class="ui input" data-inverted :data-tooltip="volume.errors.uid">
              <input class="uid-input" ref="uid" v-model.number="$attrs.value[index].uid" @input="onInput()" />
            </div>
          </sui-table-cell>
          <sui-table-cell collapsing>
            <sui-checkbox toggle v-model="$attrs.value[index].sync" />
          </sui-table-cell>
          <sui-table-cell collapsing>
            <i class="trash fitted icon" @click.prevent="remove(index)" />
          </sui-table-cell>
        </sui-table-row>
      </sui-table-body>
      <sui-table-body v-if="!hasVolumes">
        <sui-table-row>
          <sui-table-cell :colspan="field.uid ? 5 : 4">
            No volumes
          </sui-table-cell>
        </sui-table-row>
      </sui-table-body>
      <sui-table-footer>
        <sui-table-row>
          <sui-table-header-cell>
            <div class="ui tiny left labeled icon button" @click.prevent="add()">
              <i class="plus icon" />
              Add volume
            </div>
          </sui-table-header-cell>
          <sui-table-header-cell textAlign="right">
            <span v-if="field.uid">Default UID</span>
          </sui-table-header-cell>
          <sui-table-header-cell v-if="field.uid">
            <div class="ui input" v-if="field.uid">
              <input class="uid-input" v-model.number="uid" />
            </div>
          </sui-table-header-cell>
          <sui-table-header-cell colspan="2">
          </sui-table-header-cell>
        </sui-table-row>
      </sui-table-footer>
    </sui-table>
  </sui-form-field>
</template>

<script>
  import utils from '@/utils'
  import FormField from '@/mixins/FormField'

  export default {
    name: 'FormServiceVolumes',
    mixins: [
      FormField,
    ],
    props: [
      'color',
    ],
    data() {
      return {
        uid: 0,
        meta: this.$attrs.value.map(volume => {
          return {
            changed: true,
            readonly: volume.src === this.generateSrc(this.$parent.entity.name, volume.dst),
          }
        }),
      }
    },
    computed: {
      hasVolumes() {
        return Boolean(this.$attrs.value.length || this.demoVolumes.length)
      },
      demoVolumes() {
        return this.field.demo.map(volume => {
          return {
            ...volume,
            src: volume.src.replace(/\$serviceName/g, this.entity.name).toLowerCase(),
            dst: volume.dst.replace(/\$serviceName/g, this.entity.name).toLowerCase(),
          }
        }) || []
      },
      volumes() {
        return this.$attrs.value.map((volume, index) => {
          const {changed, readonly} = this.meta[index] || {changed: true, readonly: true}
          const srcHash = this.generateSrc(this.entity.name, volume.dst)
          const isAuto = volume.src === srcHash
          const errors = this.error && this.error[index] || {src: null, dst: null, uid: null}
          const hasError = changed && Boolean(errors.src || errors.dst || errors.uid)
          const hasWarning = !isAuto && !hasError
          return {
            ...volume,
            changed,
            readonly,
            isAuto,
            srcHash,
            hasWarning,
            hasError,
            errors,
          }
        })
      }
    },
    watch: {
      'entity.name'(newName, oldName) {
        this.replaceEntityName(oldName, newName)
      },
      'entity.volumesUID'(newValue) {
        this.uid = newValue
      },
      uid(newUid, oldUid) {
        this.$attrs.value.forEach((volume, index) => {
          if (volume.uid === oldUid) {
            this.$attrs.value[index].uid = newUid
          }
        })
        this.entity.volumesUID = newUid
        this.onInput()
      },
    },
    methods: {
      onBlurDst(index) {
        if (this.meta[index]) {
          this.meta[index].changed = true
        }
      },
      onInputDst(index) {
        const volume = this.volumes[index]
        if (volume.readonly) {
          this.$attrs.value[index].src = volume.srcHash
        }
        this.onInput()
      },
      lockSrc(index) {
        this.meta[index].readonly = true
        this.onInputDst(index)
        this.$nextTick(() => {
          this.$refs.dst[index].focus()
        })
      },
      unlockSrc(index) {
        return this.$modals.confirm.open({
          callback: () => {
            this.meta[index].readonly = false
            this.$nextTick(() => {
              this.$refs.src[index].focus()
            })
          },
        })
      },
      replaceEntityName(oldName, newName) {
        this.$attrs.value = this.$attrs.value.map((volume, index) => {
          if (index < this.field.default.length) {
            return {
              ...volume,
              src: this.field.default[index].src.replace(/\$serviceName/g, newName.toLowerCase()),
              dst: this.field.default[index].dst.replace(/\$serviceName/g, newName.toLowerCase()),
            }
          }
          if (volume.src === this.generateSrc(oldName, volume.dst)) {
            return {
              ...volume,
              src: this.generateSrc(newName, volume.dst),
            }
          }
          return volume
        })
        this.onInput()
      },
      add() {
        const newVolume = {src: this.generateSrc(this.entity.name.toLowerCase(), '/'), dst: '/', sync: false}
        if (this.field.uid) {
          newVolume.uid = this.uid
        }
        this.meta.push({changed: false, readonly: true})
        this.$attrs.value.push(newVolume)
        this.onInput()
        this.$nextTick(() => {
          this.$refs.dst[this.$refs.dst.length - 1].focus()
        })
      },
      remove(index) {
        this.$attrs.value.splice(index, 1)
        this.meta.splice(index, 1)
        this.onInput()
      },
      generateSrc(name, dst) {
        return `/ebs/containers/${name.toLowerCase()}/${utils.adler32(dst)}`
      },
    },
    mounted() {
      this.replaceEntityName(this.entity.name, this.entity.name)
    },
  }
</script>

<style scoped>
  .trash:hover { color: #c00; cursor: pointer; }
  input[readonly] { opacity: .5; }
  .uid-input { width: 100%!important; }
  thead th { white-space: nowrap; }
  tfoot th { border-left: none!important; font-weight: bold!important; }
</style>
